/**
 * BookX Integration for Location Booking
 * Handles location-based booking flow with custom calendar interface
 * 
 * @version 4.0
 */

/**
 * Dates blocked from booking per location (format: 'YYYY-MM-DD')
 * Add entries here whenever a location needs to close for a private event.
 */
const BLOCKED_DATES = {
  'Gruene':     ['2026-03-27', '2026-04-04', '2026-04-05', '2026-04-18', '2026-04-19', '2026-05-01', '2026-05-09'],
  'Comal':      ['2026-04-04', '2026-04-05', '2026-04-18', '2026-04-19', '2026-05-01', '2026-05-02'],
  'The Double': ['2026-04-04', '2026-04-05', '2026-04-18', '2026-04-19', '2026-05-01', '2026-05-02'],
  'The Shore':  ['2026-04-04', '2026-04-05', '2026-04-18', '2026-04-19', '2026-05-01', '2026-05-02'],
  'Huaco':      ['2026-04-04', '2026-04-05', '2026-04-18', '2026-04-19', '2026-05-01', '2026-05-02', '2026-05-09', '2026-05-10'],
  'Riverbank':  ['2026-04-04', '2026-04-05', '2026-04-18', '2026-04-19', '2026-05-01', '2026-05-02'],
};

// Clear cart if user left booking flow
(function() {
  try {
    var path = window.location.pathname;
    var inFlow = path.includes('/pages/book') || path.includes('/cart') || path.includes('/checkout') || path.includes('/pages/find-your-float');
    if (!inFlow) {
      var raw = localStorage.getItem('rockinRBookingData');
      if (raw && raw !== '{}') {
        fetch('/cart/clear.js', { method: 'POST' });
        localStorage.removeItem('rockinRBookingData');
        localStorage.removeItem('rockinRBookingMessage');
        localStorage.removeItem('rockinRBookingDisplayNeeded');
        localStorage.removeItem('rockinRBookingFormattedDate');
        localStorage.removeItem('bookingSessionTimeout');
        localStorage.removeItem('bookingSessionStartTime');
        localStorage.removeItem('bookingTimeRemaining');
        sessionStorage.removeItem('rockinRBookingData');
        sessionStorage.removeItem('selectedLocation');
      }
    }
  } catch(e) {}
})();

// Stable-partition a FormData's properties[...] entries so customer-visible
// keys come first and technical (underscore-prefixed) keys come last. Order
// within each group is preserved; non-property fields (id, quantity, …) are
// left untouched. Controls display order in the Shopify admin order detail.
function orderFormDataProperties(formData) {
  var propEntries = [];
  for (var pair of formData.entries()) {
    if (pair[0].indexOf('properties[') === 0) propEntries.push(pair);
  }
  if (propEntries.length === 0) return formData;
  var visible = [];
  var hidden = [];
  for (var i = 0; i < propEntries.length; i++) {
    var inner = propEntries[i][0].slice('properties['.length, -1);
    if (inner.charAt(0) === '_') hidden.push(propEntries[i]);
    else visible.push(propEntries[i]);
  }
  for (var j = 0; j < propEntries.length; j++) formData.delete(propEntries[j][0]);
  var ordered = visible.concat(hidden);
  for (var k = 0; k < ordered.length; k++) formData.append(ordered[k][0], ordered[k][1]);
  return formData;
}

function getMemorialDay(year) {
  // Memorial Day = Last Monday of May
  const may31 = new Date(year, 4, 31);
  const dayOfWeek = may31.getDay();
  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  return new Date(year, 4, 31 - daysToSubtract);
}

function getLaborDay(year) {
  // Labor Day = First Monday of September
  const sept1 = new Date(year, 8, 1);
  const dayOfWeek = sept1.getDay();
  const daysToAdd = dayOfWeek === 0 ? 1 : dayOfWeek === 1 ? 0 : 8 - dayOfWeek;
  return new Date(year, 8, 1 + daysToAdd);
}

function isSameDate(date1, date2) {
  return date1.getFullYear() === date2.getFullYear() &&
         date1.getMonth() === date2.getMonth() &&
         date1.getDate() === date2.getDate();
}

function isHolidayRequiringWeekendCollection(dateObj) {
  const year = dateObj.getFullYear();
  const memorialDay = getMemorialDay(year);
  const laborDay = getLaborDay(year);
  return isSameDate(dateObj, memorialDay) || isSameDate(dateObj, laborDay);
}

/**
 * Master schedule function — returns whether a location is open on a given date
 * and what time slots are available.
 *
 * MUST STAY IN SYNC with getLocationSchedule() in sections/page-booking.liquid.
 * If you change one, change the other. This copy governs the reschedule modal.
 *
 * 2026 rules:
 *   Gruene        Mar 7–May 22 Fri/Sat/Sun · May 23–Aug 23 daily · Aug 24–Nov 1 Fri/Sat/Sun (9:00–3:45)
 *   Comal         Mar 7–May 22 Fri/Sat/Sun · May 23–Aug 16 daily · Aug 17–Sep 6 Sat/Sun only · then CLOSED FOR SEASON
 *   Huaco         May 16–17 preview · May 23–Aug 16 · Aug 22–23 · Aug 30–31 · Sep 5–6 (9:00–1:45)
 *   The Shore     Mar 7–May 22 Fri/Sat/Sun · May 23–Aug 16 · Aug 22–23 · Aug 30–31
 *   First Crossing Fri/Sat/Sun through Nov 1 (9:00–11:00)
 *   Riverbank     always open
 *
 * @param {string} locationName
 * @param {Date}   dateObj
 * @returns {{ open: boolean, times: string[], reason?: string }}
 */
function getLocationSchedule(locationName, dateObj) {
  const year = dateObj.getFullYear();
  const dow  = dateObj.getDay(); // 0=Sun, 1=Mon ... 6=Sat
  const isFriSatSun = (dow === 5 || dow === 6 || dow === 0);

  const summerTimes    = ['9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '3:45 PM'];
  const huacoTimes     = ['9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '1:45 PM'];
  const springTimes    = ['11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '3:45 PM'];
  const riverbankTimes = ['9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM'];

  // Flip FC_NOON_ENABLED to true to open the 12:00 PM First Crossing slot.
  // Must be flipped in sections/page-booking.liquid at the same time.
  const FC_NOON_ENABLED = false;
  const fcTimes = ['9:00 AM', '9:30 AM', '10:30 AM', '11:00 AM'];
  if (FC_NOON_ENABLED) fcTimes.push('12:00 PM');

  const CLOSED = { open: false, times: [] };

  const mk = (m, d) => new Date(year, m, d);
  const inRange = (x, a, b) => x >= a && x <= b;

  const march7 = mk(2, 7);
  const may16  = mk(4, 16);
  const may17  = mk(4, 17);
  const may23  = mk(4, 23);
  const aug15  = mk(7, 15);
  const aug16  = mk(7, 16);
  const aug17  = mk(7, 17);
  const aug22  = mk(7, 22);
  const aug23  = mk(7, 23);
  const aug30  = mk(7, 30);
  const aug31  = mk(7, 31);
  const sept5  = mk(8, 5);
  const sept6  = mk(8, 6);   // Comal's final day of the season
  const nov1   = mk(10, 1);

  // 'The Double' was renamed to 'The Shore'. Older stored orders may still
  // carry the legacy label — alias it so those customers can reschedule.
  let loc = String(locationName || '').trim();
  if (loc === 'The Double') loc = 'The Shore';

  // ── Riverbank: always open ──
  if (loc === 'Riverbank') {
    return { open: true, times: riverbankTimes };
  }

  // ── First Crossing: Fri/Sat/Sun through Nov 1 ──
  if (/first\s*crossing/i.test(loc)) {
    // Reopened Aug 20 after the low-flow suspension. Keep in sync with page-booking.liquid.
    const FC_ENABLED = true;
    if (!FC_ENABLED) {
      return { ...CLOSED, reason: 'First Crossing floats are temporarily unavailable due to low river flow. Please contact us at info@rockinr.com to change your reservation.' };
    }

    if (dateObj > nov1) {
      return { ...CLOSED, reason: 'First Crossing floats are closed for the season.' };
    }
    if (isFriSatSun) {
      return { open: true, times: fcTimes };
    }
    return { ...CLOSED, reason: 'First Crossing floats are Fridays, Saturdays & Sundays only.' };
  }

  // ── Huaco (last reservation 1:45 PM) ──
  if (loc === 'Huaco') {
    if (isSameDate(dateObj, may16) || isSameDate(dateObj, may17)) return { open: true, times: huacoTimes };
    if (inRange(dateObj, may23, aug16)) return { open: true, times: huacoTimes };
    if (inRange(dateObj, aug22, aug23)) return { open: true, times: huacoTimes };
    if (inRange(dateObj, aug30, aug31)) return { open: true, times: huacoTimes };
    if (inRange(dateObj, sept5, sept6)) return { open: true, times: huacoTimes };
    return { ...CLOSED, reason: 'Huaco reservations are closed for the season.' };
  }

  // ── The Shore ──
  if (loc === 'The Shore') {
    if (inRange(dateObj, may23, aug16)) return { open: true, times: summerTimes };
    if (inRange(dateObj, aug22, aug23)) return { open: true, times: summerTimes };
    if (inRange(dateObj, aug30, aug31)) return { open: true, times: summerTimes };
    if (dateObj >= march7 && dateObj < may23) {
      if (isFriSatSun) return { open: true, times: springTimes };
      return { ...CLOSED, reason: 'Weekend reservations only (March 7 – May 22). Please select a Friday, Saturday, or Sunday.' };
    }
    return { ...CLOSED, reason: 'The Shore is closed for the season.' };
  }

  // ── Gruene: Fri/Sat/Sun through Nov 1, full 9:00 AM – 3:45 PM slate ──
  if (loc === 'Gruene') {
    if (dateObj >= march7 && dateObj < may23) {
      if (isFriSatSun) return { open: true, times: springTimes };
      return { ...CLOSED, reason: 'Weekend reservations only (March 7 – May 22). Please select a Friday, Saturday, or Sunday.' };
    }
    // Labor Day (Mon Sep 7, 2026) — Gruene is the only location open.
    if (isSameDate(dateObj, getLaborDay(year))) return { open: true, times: summerTimes };

    // Daily window extended one week — only one school district starts Aug 17.
    const grueneDailyEnd = mk(7, 23);
    if (inRange(dateObj, may23, grueneDailyEnd)) return { open: true, times: summerTimes };
    if (inRange(dateObj, aug15, nov1)) {
      if (isFriSatSun) return { open: true, times: summerTimes };
      return { ...CLOSED, reason: 'Fall reservations are Fridays, Saturdays & Sundays only (through November 1).' };
    }
    return { ...CLOSED, reason: 'No reservations are available at this location on this date.' };
  }

  // ── Comal: Sat/Sun only after Aug 16, closed for the season after Sep 6 ──
  if (loc === 'Comal') {
    if (dateObj >= march7 && dateObj < may23) {
      if (isFriSatSun) return { open: true, times: springTimes };
      return { ...CLOSED, reason: 'Weekend reservations only (March 7 – May 22). Please select a Friday, Saturday, or Sunday.' };
    }
    if (inRange(dateObj, may23, aug16)) return { open: true, times: summerTimes };
    if (inRange(dateObj, aug17, sept6)) {
      if (dow === 6 || dow === 0) return { open: true, times: summerTimes };
      return { ...CLOSED, reason: 'Comal reservations are Saturdays & Sundays only for the rest of the season.' };
    }
    return { ...CLOSED, reason: 'The Comal is closed for the season.' };
  }

  return { ...CLOSED, reason: 'Reservations are not available on this date.' };
}

/**
 * Main booking class for location-based reservations
 */
class BookXLocationBooking {
  constructor() {
    this.selectedLocation = null;
    this.bookingData = null;
    this.debugMode = false;
    this.buttonsSetup = false;
    this.notificationCheckInterval = null;
    
    this.init();
  }

  init() {
    this.injectEarlyLoadingScript();    
    
    setTimeout(() => {
      this.findAndSetupBookingButtons();
      this.showBookingNotificationIfNeeded();
      this.setupCartListeners();
    }, 1000);
  }

  checkAndClearAbandonedCart() {
    const path = window.location.pathname;
    const isInBookingFlow = (
      path.includes('/pages/book') ||
      path.includes('/cart') ||
      path.includes('/checkout') ||
      path.includes('/pages/find-your-float')
    );
    const hasBookingData = !!BookXLocationBooking.getBookingData();

    if (!isInBookingFlow && hasBookingData) {
      fetch('/cart/clear.js', { method: 'POST' }).catch(() => {});
      BookXLocationBooking.clearBookingData();
    }
  }

  findAndSetupBookingButtons() {
    if (this.buttonsSetup) return;
    
    let buttonsFound = 0;
    const allElements = document.querySelectorAll('a, button');
    
    allElements.forEach((element) => {
      const text = element.textContent?.trim().toUpperCase();
      
      if (text === 'BOOK NOW!' && !element.dataset.bookingSetup) {
        const locationName = this.extractLocationFromButton(element);
        this.setupBookingButton(element, locationName);
        buttonsFound++;
      }
    });
    
    if (buttonsFound > 0) {
      this.buttonsSetup = true;
    }
  }

  extractLocationFromButton(button) {
    if (button.dataset.location) {
      return button.dataset.location;
    }
    
    let currentElement = button.parentElement;
    let attempts = 0;
    
    while (currentElement && attempts < 5) {
      const text = currentElement.textContent || '';
      const lowerText = text.toLowerCase();
      
      if (lowerText.includes('gruene') && !lowerText.includes('double')) return 'Gruene';
      if (lowerText.includes('the shore') || lowerText.includes('the-shore')) return 'The Shore';
      if (lowerText.includes('the-double') || lowerText.includes('the double')) return 'The Double';
      if (lowerText.includes('comal') && !lowerText.includes('river')) return 'Comal';
      if (lowerText.includes('riverbank')) return 'Riverbank';
      if (lowerText.includes('huaco')) return 'Huaco';
      
      currentElement = currentElement.parentElement;
      attempts++;
    }
    
    const allBookNowButtons = Array.from(document.querySelectorAll('a, button')).filter(el => 
      el.textContent?.trim().toUpperCase() === 'BOOK NOW!'
    );
    
    const buttonIndex = allBookNowButtons.indexOf(button);
    const locationOrder = ['Gruene', 'The Shore', 'Comal', 'Riverbank', 'Huaco'];
    
    if (buttonIndex >= 0 && buttonIndex < locationOrder.length) {
      return locationOrder[buttonIndex];
    }
    
    return `Location ${buttonIndex + 1}`;
  }

  setupBookingButton(button, locationName) {
    button.dataset.bookingSetup = 'true';
    
    button.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      this.selectedLocation = locationName;
      sessionStorage.setItem('selectedLocation', locationName);
      
      this.showLocationAlert(`Opening booking calendar for ${locationName}!`);
      this.showBookingCalendar(locationName);
    });
    
    button.style.transition = 'all 0.3s ease';
    button.addEventListener('mouseenter', () => {
      button.style.transform = 'scale(1.05)';
      button.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
    });
    button.addEventListener('mouseleave', () => {
      button.style.transform = 'scale(1)';
      button.style.boxShadow = 'none';
    });
  }

  /**
   * Generate <option> elements for the time dropdown.
   * If a dateObj is provided, uses the schedule for that date.
   * Without a date, falls back to a neutral placeholder.
   */
  generateTimeOptions(locationName, dateObj = null) {
    let times = [];

    if (dateObj) {
      const schedule = getLocationSchedule(locationName, dateObj);
      times = schedule.open ? schedule.times : [];
    } else {
      // Default before date is picked: show summer times as a preview
      times = ['9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '3:45 PM'];
      if (locationName === 'Riverbank') {
        times = ['9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM'];
      }
    }

    let html = '<option value="">Select time...</option>';
    times.forEach(t => {
      html += `<option value="${t}">${t}</option>`;
    });
    return html;
  }

 async loadFlatpickr() {
  if (window.flatpickr) return;
  await new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdnjs.cloudflare.com/ajax/libs/flatpickr/4.6.13/flatpickr.min.css';
    document.head.appendChild(link);
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/flatpickr/4.6.13/flatpickr.min.js';
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

  

  showBookingCalendar(locationName) {
    this.selectedLocation = locationName;
    
    const logoUrl = "//rockinrtubing.myshopify.com/cdn/shop/files/Rockin-R-Logo.png?v=1741816251";
    const calendarModal = document.createElement('div');
    calendarModal.className = 'rockinr-booking-modal-overlay';
    calendarModal.innerHTML = `
      <div class="rockinr-booking-modal-overlay" style="
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(60, 62, 67, 0.85);
        z-index: 10001;
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: Arial, sans-serif;
        animation: fadeIn 0.3s ease;
      ">
        <div class="rockinr-booking-modal-content" style="
          background: #fff;
          padding: 0;
          border-radius: 16px;
          max-width: 500px;
          width: 90%;
          box-shadow: 0 20px 60px rgba(60, 62, 67, 0.4);
          overflow: hidden;
          max-height: 90vh;
          overflow-y: auto;
          position: relative;
        ">
          <!-- Logo inside modal -->
          <div style="position: absolute; top: 18px; left: 18px; z-index: 2;">
            <img src="${logoUrl}" alt="Rockin' R Logo" style="width: 44px; height: 44px; object-fit: contain; background: none; border: none; display: block;" />
          </div>
          <!-- Header -->
          <div style="
            background: #9ABCC7;
            color:rgb(39, 39, 37);
            padding: 25px 30px 25px 75px;
            text-align: center;
            position: relative;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          ">
            <button class="rockinr-booking-modal-close" style="
              position: absolute;
              top: 15px;
              right: 20px;
              background: rgba(60,62,67,0.08);
              border: none;
              color:rgb(38, 35, 35);
              width: 32px;
              height: 32px;
              border-radius: 50%;
              cursor: pointer;
              font-size: 18px;
              display: flex;
              align-items: center;
              justify-content: center;
              transition: background-color 0.3s ease;
            " onmouseover="this.style.background='rgba(60,62,67,0.15)'" onmouseout="this.style.background='rgba(60,62,67,0.08)'">×</button>
            <h2 style="margin: 0; font-size: 24px; font-weight: 600; width: 100%; text-align: center;">${locationName} location</h2>
            <p style="margin: 8px 0 0 0; opacity: 0.9; font-size: 16px; width: 100%; text-align: center;">
              <a href="https://rockinrtubing.myshopify.com/pages/map" target="_blank" style="color: #222; text-decoration: underline;">Click here for a map of our locations</a>
            </p>
          </div>
          
          <!-- Content -->
          <div style="padding: 30px;">

            <!-- Date Selection -->
            <div style="margin-bottom: 25px;">
              <label style="display: block; margin-bottom: 8px; font-weight: 600; color: #222; font-size: 16px;">What date would you visit us?:</label>
              <input type="text" id="booking-date" readonly placeholder="Select a date..." style="width: 100%; padding: 14px; border: 2px solid #eee; border-radius: 8px; font-size: 16px; transition: border-color 0.3s ease; background: #fff; color: #222;" min="${new Date().toISOString().split('T')[0]}">
            </div>

            <!-- Time Selection -->
            <div style="margin-bottom: 25px;">
              <label style="display: block; margin-bottom: 8px; font-weight: 600; color: #222; font-size: 16px;">Booking Time:</label>
              <select id="booking-time" style="width: 100%; padding: 14px; border: 2px solid #eee; border-radius: 8px; font-size: 16px; background: #fff; color: #222; transition: border-color 0.3s ease;">
                ${this.generateTimeOptions(locationName)}
              </select>
            </div>

            <!-- Phone Number -->
            <div style="margin-bottom: 25px;">
              <label style="display: block; margin-bottom: 8px; font-weight: 600; color: #222; font-size: 16px;">Contact phone number:</label>
              <input type="tel" id="booking-phone" style="width: 100%; padding: 14px; border: 2px solid #eee; border-radius: 8px; font-size: 16px; transition: border-color 0.3s ease; background: #fff; color: #222;" placeholder="(123) 456-7890" required>
              <div style="margin-top: 5px; font-size: 13px; color: #666; font-style: italic;">We'll only use this to contact you about your booking if needed</div>
            </div>

            <!-- Guest Count -->
            <div style="margin-bottom: 25px;">
              <label style="display: block; margin-bottom: 8px; font-weight: 600; color: #222; font-size: 16px;">Number of guests:</label>
              <input type="number" id="booking-guests" min="1" max="50" style="width: 100%; padding: 14px; border: 2px solid #eee; border-radius: 8px; font-size: 16px; background: #fff; color: #222;" placeholder="How many people in your group?" required>
              <div style="margin-top: 5px; font-size: 13px; color: #666; font-style: italic;">Include all adults and children</div>
            </div>

            <!-- Rate Info (shown after date selected) -->
            <div id="rate-info" style="background: #f5f5f5; border: 1px solid #eee; border-radius: 8px; padding: 15px; margin-bottom: 25px; display: none;">
              <div style="font-weight: 600; color: #222; margin-bottom: 5px;">Expert Tips:</div>
              <div id="rate-details" style="color: #222; font-size: 14px;"></div>
            </div>

            <!-- Action Buttons -->
            <div style="display: flex; gap: 12px;">
              <button onclick="window.bookXInstance.processBooking()" style="flex: 1; background: #FCCA1E; color: #3E3E03; border: none; padding: 16px; border-radius: 8px; cursor: pointer; font-weight: 600; font-size: 16px; transition: background 0.2s, color 0.2s;" onmouseover="this.style.background='#FCCA1E'; this.style.color='#222'" onmouseout="this.style.background='#FCCA1E'; this.style.color='#3E3E03'">
                Choose your experience →
              </button>
              <button class="rockinr-booking-cancel-btn" style="background:#9a0705; color: #fff; border: none; padding: 16px 24px; border-radius: 8px; cursor: pointer; font-weight: 600; transition: background-color 0.3s ease;" onmouseover="this.style.background='#A74F05'" onmouseout="this.style.background='#9a0705'">
                Cancel
              </button>
            </div>

          </div><!-- /Content -->
        </div><!-- /modal-content -->
      </div><!-- /modal-overlay -->
      <style>
      @keyframes fadeIn {
        from { opacity: 0; transform: scale(0.95); }
        to { opacity: 1; transform: scale(1); }
      }
      #booking-date:focus, #booking-time:focus, #booking-phone:focus {
        outline: none;
        border-color: #9ABCC7 !important;
        box-shadow: 0 0 0 3px rgba(154, 188, 199, 0.2);
      }
      </style>
    `;
    document.body.appendChild(calendarModal);
    
    calendarModal.querySelector('.rockinr-booking-modal-close').onclick = function() {
      calendarModal.remove();
    };
    
    const overlay = calendarModal.querySelector('.rockinr-booking-modal-overlay');
    const modalContent = calendarModal.querySelector('.rockinr-booking-modal-content');
    overlay.addEventListener('mousedown', function(e) {
      if (!modalContent.contains(e.target)) {
        calendarModal.remove();
      }
    });
    
    const cancelBtn = calendarModal.querySelector('.rockinr-booking-cancel-btn');
    if (cancelBtn) {
      cancelBtn.addEventListener('click', function() {
        calendarModal.remove();
      });
    }
    
    const dateInput  = document.getElementById('booking-date');
    const timeSelect = document.getElementById('booking-time');
    const rateInfo   = document.getElementById('rate-info');
    const rateDetails = document.getElementById('rate-details');
    const bookingInstance = this;
    
    this.loadFlatpickr().then(() => {
  const blockedForLocation = BLOCKED_DATES[bookingInstance.selectedLocation] || [];

  flatpickr(dateInput, {
  minDate: 'today',
  dateFormat: 'Y-m-d',
  disableMobile: true,
  allowInput: false,
  disable: [
  function(date) {
    // Format date using LOCAL parts to avoid UTC timezone shift
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const dateStr = `${y}-${m}-${d}`;

    // Check blocked dates
    if (blockedForLocation.includes(dateStr)) return true;

    // Check schedule
    const schedule = getLocationSchedule(bookingInstance.selectedLocation, date);
    return !schedule.open;
  }
],
  onReady: function(selectedDates, dateStr, instance) {
    instance.altInput && (instance.altInput.readOnly = true); 
    instance._input.readOnly = true;
  },

    onChange: function(selectedDates, dateStr, instance) {
      if (!dateStr) {
        rateInfo.style.display = 'none';
        timeSelect.innerHTML = bookingInstance.generateTimeOptions(bookingInstance.selectedLocation);
        return;
      }

      // ── Blocked date check inside onChange — catches any timezone slip-through ──
      const y = selectedDates[0].getFullYear();
      const m = String(selectedDates[0].getMonth() + 1).padStart(2, '0');
      const d = String(selectedDates[0].getDate()).padStart(2, '0');
      const localDateStr = `${y}-${m}-${d}`;

      if (blockedForLocation.includes(localDateStr)) {
        // Clear selection immediately
        instance.clear();
        rateInfo.style.display = 'none';
        timeSelect.innerHTML = bookingInstance.generateTimeOptions(bookingInstance.selectedLocation);

        // Show popup
        const popup = document.createElement('div');
        popup.innerHTML = `
          <div style="position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);z-index:20000;display:flex;align-items:center;justify-content:center;">
            <div style="background:#fff;border-radius:12px;padding:30px;max-width:360px;width:90%;text-align:center;box-shadow:0 8px 32px rgba(0,0,0,0.3);">
              <div style="font-size:40px;margin-bottom:12px;">🚫</div>
              <h3 style="margin:0 0 10px;color:#c62828;font-size:18px;">Date Unavailable</h3>
              <p style="margin:0 0 20px;color:#555;font-size:14px;">
                The <strong>${bookingInstance.selectedLocation}</strong> location is closed on this date.<br>Please select a different date.
              </p>
              <button onclick="this.closest('[data-rnr-popup]').remove()" style="background:#FCCA1E;color:#3E3E03;border:none;padding:12px 28px;border-radius:8px;font-weight:600;font-size:15px;cursor:pointer;">
                Choose Another Date
              </button>
            </div>
          </div>
        `;
        popup.setAttribute('data-rnr-popup', 'true');
        document.body.appendChild(popup);
        return;
      }

      // ── Valid date — update time slots and tips ──
      const date = new Date(y, parseInt(m) - 1, parseInt(d));
      dateInput.style.borderColor = '#eee';
      timeSelect.innerHTML = bookingInstance.generateTimeOptions(bookingInstance.selectedLocation, date);
      const dow = date.getDay();
      rateInfo.style.display = 'block';
      rateInfo.style.background = '#f5f5f5';
      rateInfo.style.borderColor = '#eee';
      rateInfo.style.color = '#222';
      if (dow === 0 || dow === 6) {
        rateDetails.innerHTML = '<b>Expert Tips</b><br>• Fill your waiver out online ahead of time for a faster experience!<br>• This is a weekend experience';
      } else {
        rateDetails.innerHTML = '<b>Expert Tips</b><br>• Fill your waiver out online ahead of time for a faster experience!';
      }
    }
  });
});

    const phoneInput = document.getElementById('booking-phone');
    if (phoneInput) {
      phoneInput.addEventListener('input', (e) => {
        const phone = e.target.value;
        const isValid = this.validatePhoneNumber(phone);
        
        if (phone.length > 0) {
          phoneInput.style.borderColor = isValid ? '#4caf50' : '#f44336';
        } else {
          phoneInput.style.borderColor = '#ddd';
        }
      });

      phoneInput.addEventListener('focus', () => {
        if (!phoneInput.value || this.validatePhoneNumber(phoneInput.value)) {
          phoneInput.style.borderColor = '#FCCA1E'; 
        }
      });

      phoneInput.addEventListener('blur', () => {
        const phone = phoneInput.value;
        if (phone.length > 0 && !this.validatePhoneNumber(phone)) {
          phoneInput.style.borderColor = '#f44336'; 
        } else if (phone.length > 0) {
          phoneInput.style.borderColor = '#4caf50'; 
        } else {
          phoneInput.style.borderColor = '#ddd'; 
        }
      });
    }
    
    setTimeout(() => {
      dateInput.focus();
    }, 300);
  }

  showLocationAlert(message) {
    const existingAlerts = document.querySelectorAll('.rockin-r-booking-alert');
    existingAlerts.forEach(alert => alert.remove());
    
    const alert = document.createElement('div');
    alert.className = 'rockin-r-booking-alert';
    alert.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: #FCCA1E;
        color: white;
        padding: 15px 20px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        z-index: 10002;
        font-family: Arial, sans-serif;
        max-width: 300px;
        line-height: 1.4;
        animation: slideIn 0.3s ease;
      ">
        ${message}
      </div>
      <style>
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      </style>
    `;
    
    document.body.appendChild(alert);
    
    setTimeout(() => {
      if (alert.parentNode) {
        alert.style.opacity = '0';
        alert.style.transform = 'translateX(100%)';
        setTimeout(() => alert.remove(), 300);
      }
    }, 5000);
  }

  validatePhoneNumber(phone) {
    const cleaned = phone.replace(/\D/g, '');
    
    if (cleaned.length === 10) {
      return true;
    } else if (cleaned.length === 11 && cleaned[0] === '1') {
      return true;
    } else if (cleaned.length >= 10 && cleaned.length <= 15) {
      return true;
    }
    
    return false;
  }

  processBooking() {
    const date  = document.getElementById('booking-date').value;
    const phone = document.getElementById('booking-phone').value;
    const time  = document.getElementById('booking-time').value;

    const showError = (html, focusEl = null) => {
      const existing = document.querySelector('[data-booking-error]');
      if (existing) existing.remove();
      const errorDiv = document.createElement('div');
      errorDiv.setAttribute('data-booking-error', 'true');
      errorDiv.innerHTML = `<div style="background:#ffebee;border:1px solid #ffcdd2;color:#c62828;padding:12px;border-radius:8px;margin:10px 0;text-align:center;font-weight:500;">${html}</div>`;
      const buttons = document.querySelector('[style*="display: flex; gap: 12px"]');
      if (buttons) buttons.parentNode.insertBefore(errorDiv, buttons);
      setTimeout(() => errorDiv.remove(), 5000);
      if (focusEl) focusEl.focus();
    };

    const guests = parseInt(document.getElementById('booking-guests')?.value, 10);
    if (!guests || isNaN(guests) || guests < 1) {
      showError('Please enter the number of guests to continue booking!', document.getElementById('booking-guests'));
      return;
    }

    if (!date || !time) {
      showError('Please select a date and time to continue booking!');
      return;
    }

    // ── Blocked date — checked FIRST ──
    const blocked = (BLOCKED_DATES[this.selectedLocation] || []);
    if (blocked.includes(date)) {
      showError(`The ${this.selectedLocation} location is closed on this date. Please select a different date.`);
      return;
    }

    // ── Schedule validation ──
    const [yr, mo, dy] = date.split('-').map(Number);
    const chosenDate = new Date(yr, mo - 1, dy);
    const schedule = getLocationSchedule(this.selectedLocation, chosenDate);
    if (!schedule.open) {
      showError(schedule.reason || 'Reservations are not available on this date.');
      return;
    }

    if (!phone || phone.trim() === '') {
      showError('Please enter your phone number to continue booking!', document.getElementById('booking-phone'));
      return;
    }

    if (!this.validatePhoneNumber(phone)) {
      showError('Please enter a valid phone number (10 digits minimum).<br><small style="font-size:0.9em;opacity:0.9;">Example: (555) 123-4567 or 555-123-4567</small>', document.getElementById('booking-phone'));
      return;
    }

    const bookingData = {
      location:  this.selectedLocation,
      date:      date,
      phone:     phone,
      time:      time,
      guests:    guests,
      timestamp: new Date().toISOString()
    };

    const dayOfWeek = chosenDate.getDay();
    const isHoliday = isHolidayRequiringWeekendCollection(chosenDate);
    const isNBCityLocation = ['Gruene', 'The Shore', 'The Double', 'Comal'].includes(this.selectedLocation);

    bookingData.isWeekend  = (dayOfWeek === 6 || dayOfWeek === 0) || (isHoliday && isNBCityLocation);
    bookingData.dayOfWeek  = dayOfWeek;
    bookingData.isHoliday  = isHoliday;

    sessionStorage.setItem('rockinRBookingData', JSON.stringify(bookingData));
    localStorage.setItem('rockinRBookingData', JSON.stringify(bookingData));

    this.bookingData = bookingData;
    this.storeBookingBannerData();
    this.injectEarlyLoadingScript();
    this.showBookingNotificationIfNeeded();

    const modal = document.querySelector('[style*="position: fixed"]');
    if (modal) { modal.style.opacity = '0'; modal.style.transform = 'scale(0.95)'; setTimeout(() => modal.remove(), 300); }

    const dateObj = new Date(date);
    const formattedDate = dateObj.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    this.createBookingAlert(`You're booking for ${formattedDate} at ${time} at ${this.selectedLocation}!`);

    setTimeout(() => this.redirectToLocationCollection(), 500);
  }

  createBookingAlert(message) {
    const existingAlerts = document.querySelectorAll('.rockin-r-booking-alert');
    existingAlerts.forEach(alert => alert.remove());
    
    const alert = document.createElement('div');
    alert.className = 'rockin-r-booking-alert';
    alert.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: #FCCA1E;
        color: white;
        padding: 15px 20px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        z-index: 10002;
        font-family: Arial, sans-serif;
        max-width: 300px;
        line-height: 1.4;
        animation: slideIn 0.3s ease;
      ">
        ${message}
      </div>
      <style>
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      </style>
    `;
    
    document.body.appendChild(alert);
    
    setTimeout(() => {
      if (alert.parentNode) {
        alert.style.opacity = '0';
        alert.style.transform = 'translateX(100%)';
        setTimeout(() => alert.remove(), 300);
      }
    }, 5000);
  }

  redirectToLocationCollection() {
    const isWeekend = this.bookingData && this.bookingData.isWeekend;
    
    const locationCollections = {
      'Gruene': {
        weekday: '/collections/gruene',
        weekend: '/collections/gruene-weekend'
      },
      'The Shore': {
        weekday: '/collections/double',
        weekend: '/collections/double-weekend'
      },
      'The Double': {
        weekday: '/collections/double',
        weekend: '/collections/double-weekend'
      },
      'Comal': {
        weekday: '/collections/comal',
        weekend: '/collections/comal-weekend'
      },
      'Riverbank': {
        weekday: '/collections/riverbank',
        weekend: '/collections/riverbank-weekend',
        weekendDisplay: '(Weekend)'
      },
      'Huaco': {
        weekday: '/collections/huaco',
        weekend: '/collections/huaco-weekend',
        weekendDisplay: '(Weekend)'
      }
    };
    
    const locationConfig = locationCollections[this.selectedLocation];
    if (!locationConfig) {
      this.showLocationAlert(`Collection not found for "${this.selectedLocation}"`);
      return;
    }
    
    const collectionUrl  = isWeekend && locationConfig.weekend ? locationConfig.weekend : locationConfig.weekday;
    const collectionType = isWeekend && locationConfig.weekend ? 'Weekend' : 'Weekday';
    
    const displayLocation = isWeekend && locationConfig.weekendDisplay 
        ? `${this.selectedLocation} ${locationConfig.weekendDisplay}`
        : this.selectedLocation;
    
    const params = new URLSearchParams();
    params.set('booking', 'confirmed');
    params.set('location', this.selectedLocation);
    params.set('type', collectionType.toLowerCase());
    
    if (this.bookingData) {
      if (this.bookingData.date)      params.set('date', this.bookingData.date);
      if (this.bookingData.isWeekend !== undefined) params.set('weekend', this.bookingData.isWeekend.toString());
      if (this.bookingData.dayOfWeek !== undefined) params.set('dayOfWeek', this.bookingData.dayOfWeek.toString());
    }
    
    const fullUrl = `${collectionUrl}?${params.toString()}`;
    
    this.showLocationAlert(`Loading ${collectionType} products for ${displayLocation}!`);
    
    setTimeout(() => {
      try {
        if (window.location.assign) {
          window.location.assign(fullUrl);
        } else {
          window.location.href = fullUrl;
        }
        
        setTimeout(() => {
          if (window.location.href.indexOf(collectionUrl) === -1) {
            window.location.replace(fullUrl);
          }
        }, 2000);
        
      } catch (error) {
        this.showRedirectError(fullUrl);
      }
    }, 500);
  }

  showWeekendUnavailableMessage(location) {
    const existingModal = document.querySelector('.rockinr-booking-modal-overlay');
    if (existingModal) {
      existingModal.remove();
    }
    
    const unavailableModal = document.createElement('div');
    unavailableModal.className = 'rockinr-booking-modal-overlay';
    unavailableModal.innerHTML = `
      <div class="rockinr-booking-modal-overlay" style="
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(60, 62, 67, 0.85);
        z-index: 10001;
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: Arial, sans-serif;
        animation: fadeIn 0.3s ease;
      ">
        <div class="rockinr-booking-modal-content" style="
          background: #fff;
          padding: 25px;
          border-radius: 12px;
          max-width: 450px;
          width: 90%;
          max-height: 60vh;
          box-shadow: 0 10px 40px rgba(60, 62, 67, 0.3);
          position: relative;
          text-align: center;
        ">
          <button class="rockinr-weekend-modal-close" style="
            position: absolute;
            top: 15px;
            right: 15px;
            background: #f5f5f5;
            border: none;
            color: #333;
            width: 28px;
            height: 28px;
            border-radius: 50%;
            cursor: pointer;
            font-size: 16px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: background-color 0.3s ease;
          " onmouseover="this.style.background='#e0e0e0'" onmouseout="this.style.background='#f5f5f5'">×</button>
          
          <h3 style="margin: 0 0 15px 0; color: #333; font-size: 18px;">
            Weekend bookings are not available at ${location}
          </h3>
          
          <div style="
            background: #fff3cd;
            border: 1px solid #ffeaa7;
            border-radius: 6px;
            padding: 15px;
            margin: 15px 0;
            text-align: left;
          ">
            <h4 style="margin: 0 0 8px 0; color: #856404; font-size: 14px;">Available Booking Days:</h4>
            <ul style="margin: 0; color: #856404; font-size: 13px; line-height: 1.5;">
              <li><strong>Monday through Friday</strong> - All day availability</li>
              <li><strong>Weekends (Sat & Sun)</strong> - Not available for booking</li>
            </ul>
          </div>
          
          <div style="
            background: #d1ecf1;
            border: 1px solid #bee5eb;
            border-radius: 6px;
            padding: 15px;
            margin: 15px 0;
          ">
            <h4 style="margin: 0 0 6px 0; color: #0c5460; font-size: 14px;">Alternative Options:</h4>
            <p style="margin: 0; color: #0c5460; font-size: 13px;">
              For weekend river trips, consider our <strong>Gruene</strong>, <strong>The Shore</strong>, or <strong>Comal</strong> locations!
            </p>
          </div>
          
          <div style="display: flex; gap: 10px; margin-top: 20px;">
            <button onclick="window.location.href='/pages/locations'" style="
              flex: 1;
              background: #9ABCC7;
              color: #333;
              border: none;
              padding: 12px;
              border-radius: 6px;
              cursor: pointer;
              font-weight: 600;
              font-size: 14px;
              transition: background 0.2s;
            " onmouseover="this.style.background='#8AADB8'" onmouseout="this.style.background='#9ABCC7'">
              View All Locations
            </button>
            <button class="rockinr-weekend-close-btn" style="
              background: #6c757d;
              color: white;
              border: none;
              padding: 12px 16px;
              border-radius: 6px;
              cursor: pointer;
              font-weight: 600;
              font-size: 14px;
              transition: background-color 0.3s ease;
            " onmouseover="this.style.background='#5a6268'" onmouseout="this.style.background='#6c757d'">
              Close
            </button>
          </div>
        </div>
      </div>
      <style>
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      </style>
    `;
    
    document.body.appendChild(unavailableModal);
    
    unavailableModal.querySelector('.rockinr-weekend-modal-close').onclick = function() {
      unavailableModal.remove();
    };
    
    unavailableModal.querySelector('.rockinr-weekend-close-btn').onclick = function() {
      unavailableModal.remove();
    };
    
    const overlay = unavailableModal.querySelector('.rockinr-booking-modal-overlay');
    const modalContent = unavailableModal.querySelector('.rockinr-booking-modal-content');
    overlay.addEventListener('mousedown', function(e) {
      if (!modalContent.contains(e.target)) {
        unavailableModal.remove();
      }
    });
  }

  showRedirectError(attemptedUrl) {
    const errorAlert = document.createElement('div');
    errorAlert.innerHTML = `
      <div style="
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: white;
        border: 2px solid #f44336;
        border-radius: 12px;
        padding: 25px;
        box-shadow: 0 8px 32px rgba(0,0,0,0.3);
        z-index: 10003;
        max-width: 400px;
        text-align: center;
        font-family: Arial, sans-serif;
      ">
        <h3 style="margin: 0 0 15px 0; color: #f44336;">Redirect Issue</h3>
        <p style="margin: 0 0 20px 0; color: #666;">
          Automatic redirect failed. Click below to continue manually:
        </p>
        <a href="${attemptedUrl}" style="
          display: inline-block;
          background: #FCCA1E;
          color: white;
          text-decoration: none;
          padding: 12px 24px;
          border-radius: 6px;
          font-weight: 600;
          margin-right: 10px;
        ">Go to Products</a>
        <button onclick="this.parentElement.parentElement.remove()" style="
          background: #f44336;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 600;
        ">Close</button>
      </div>
      <div style="
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0,0,0,0.5);
        z-index: 10002;
      " onclick="this.parentElement.remove()"></div>
    `;
    
    document.body.appendChild(errorAlert);
    
    setTimeout(() => {
      if (errorAlert.parentNode) {
        errorAlert.remove();
      }
    }, 10000);
  }

  showBookingNotificationIfNeeded() {
    return;
  }
  
  startNotificationVisibilityChecker() {
    // Banner disabled
  }
  
  addBookingNoticeToProduct(bookingData, formattedDate) {
    setTimeout(() => {
      const addToCartButton = document.querySelector('button[name="add"], input[name="add"]');
      if (!addToCartButton) return;
      
      const productForm = addToCartButton.closest('form') || addToCartButton.parentElement;
      if (!productForm) return;
      
      if (productForm.querySelector('.booking-product-notice')) return;
      
      const notice = document.createElement('div');
      notice.className = 'booking-product-notice';
      
      const randomInventory = Math.floor(Math.random() * 4) + 2;
      
      notice.innerHTML = `
        <div style="
          margin: 15px 0;
          padding: 15px;
          background: #f8f4e3;
          border: 2px solid #FCCA1E;
          border-radius: 8px;
          text-align: center;
        ">
          <p style="
            margin: 0 0 8px 0;
            font-size: 16px;
            color: #3E3E03;
            font-weight: 600;
          ">
            Adding this product to your booking for<br>
            <strong>${formattedDate}</strong> at <strong>${bookingData.location || 'Selected Location'}</strong>
          </p>
          
          <div style="display: flex; justify-content: center; align-items: center; gap: 15px; flex-wrap: wrap; margin-top: 10px;">
            <div style="
              display: flex; 
              align-items: center; 
              gap: 5px; 
              background-color: rgba(154, 7, 5, 0.1); 
              padding: 4px 10px;
              border-radius: 4px;
              color: #9a0705;
              font-weight: bold;
              font-size: 14px;
            ">
              <span style="animation: pulse 1.5s infinite;">⚠️</span>
              <span>Only <strong>${randomInventory}</strong> left for this date!</span>
            </div>
            
            <div style="
              display: flex; 
              align-items: center; 
              gap: 5px; 
              background-color: rgba(0, 128, 0, 0.1); 
              padding: 4px 10px;
              border-radius: 4px;
              color: #006400;
              font-weight: bold;
              font-size: 14px;
            ">
              <span>✅</span>
              <span>Popular choice for ${bookingData.isWeekend ? 'weekends' : 'weekdays'}</span>
            </div>
          </div>
        </div>
        <style>
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.6; }
            100% { opacity: 1; }
          }
        </style>
      `;
      
      productForm.insertBefore(notice, addToCartButton);
      
      setTimeout(() => {
        if (Math.random() > 0.5) {
          const recentBooking = document.createElement('div');
          recentBooking.className = 'recent-booking-alert';
          recentBooking.innerHTML = `
            <div style="
              background-color: rgba(0, 128, 0, 0.9);
              color: white;
              position: fixed;
              bottom: 20px;
              right: 20px;
              padding: 10px 15px;
              border-radius: 6px;
              box-shadow: 0 4px 12px rgba(0,0,0,0.2);
              z-index: 10000;
              font-size: 14px;
              max-width: 250px;
              animation: slideInFromBottom 0.5s ease-out;
              display: flex;
              align-items: center;
              gap: 10px;
            ">
              <div style="
                width: 12px;
                height: 12px;
                background-color: #50C878;
                border-radius: 50%;
                position: relative;
              ">
                <span style="
                  position: absolute;
                  top: -4px;
                  left: -4px;
                  width: 20px;
                  height: 20px;
                  background-color: rgba(80, 200, 120, 0.5);
                  border-radius: 50%;
                  animation: ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite;
                "></span>
              </div>
              <span>
                <strong>Just booked!</strong><br>Someone from ${getRandomCity()} just booked this experience for ${formattedDate}
              </span>
            </div>
            <style>
              @keyframes slideInFromBottom {
                0% { transform: translateY(100%); opacity: 0; }
                100% { transform: translateY(0); opacity: 1; }
              }
              @keyframes ping {
                75%, 100% {
                  transform: scale(2);
                  opacity: 0;
                }
              }
            </style>
          `;
          
          document.body.appendChild(recentBooking);
          
          setTimeout(() => {
            if (recentBooking.parentNode) {
              recentBooking.style.opacity = '0';
              recentBooking.style.transform = 'translateY(20px)';
              recentBooking.style.transition = 'opacity 0.5s, transform 0.5s';
              setTimeout(() => recentBooking.remove(), 500);
            }
          }, 7000);
        }
      }, Math.random() * 15000 + 5000);
    }, 1000);
  }
  
  addBookingSummaryToCart(bookingData, formattedDate) {
    const cartForm = document.getElementById('Cart');
    const checkoutForm = document.querySelector('.checkout_content');
    
    if (!cartForm && !checkoutForm) return;
    
    const targetElement = cartForm || checkoutForm;
    const isCheckoutPage = window.location.pathname.includes('/checkout');
    
    const summary = document.createElement('div');
    summary.className = 'booking-cart-summary';
    summary.innerHTML = `
      <div style="
        margin: 20px 0;
        padding: 15px;
        background: #f8f4e3;
        border: 2px solid #FCCA1E;
        border-radius: 8px;
        position: relative;
        overflow: hidden;
      ">
        ${isCheckoutPage ? '<div style="position: absolute; top: 10px; right: 10px; background: #9a0705; color: white; padding: 3px 8px; border-radius: 4px; font-size: 12px; animation: pulse 1.5s infinite;">Completing checkout...</div>' : ''}
        
        <h3 style="
          margin-top: 0;
          margin-bottom: 10px;
          font-size: 18px;
          color: #3E3E03;
          display: flex;
          align-items: center;
          gap: 8px;
        ">
          <span>📅</span> Your Booking Details
        </h3>
        
        <p style="
          margin: 0;
          font-size: 16px;
          color: #3E3E03;
        ">
          <strong>Date:</strong> ${formattedDate}<br>
          <strong>Location:</strong> ${bookingData.location || 'Not specified'}<br>
          <strong>Experience:</strong> ${bookingData.isWeekend ? 'Weekend rates apply' : 'Weekday rates apply'}
        </p>
        
        <div style="
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px dashed #E5B50F;
          display: ${isCheckoutPage ? 'flex' : 'none'};
          gap: 10px;
          flex-wrap: wrap;
        ">
          <div style="
            flex: 1;
            min-width: 200px;
            display: flex;
            align-items: center;
            gap: 8px;
            background: rgba(154, 7, 5, 0.1);
            padding: 8px;
            border-radius: 4px;
            color: #9a0705;
          ">
            <span style="font-size: 20px;">⏱️</span>
            <div>
              <strong>Checkout timer</strong><br>
              <span id="checkout-timer">completing payment...</span>
            </div>
          </div>
          
          <div style="
            flex: 1;
            min-width: 200px;
            display: flex;
            align-items: center;
            gap: 8px;
            background: rgba(0, 128, 0, 0.1);
            padding: 8px;
            border-radius: 4px;
            color: #006400;
          ">
            <span style="font-size: 20px;">✅</span>
            <div>
              <strong>Secure your spot</strong><br>
              Dates filling up quickly!
            </div>
          </div>
        </div>
      </div>
      <style>
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.6; }
          100% { opacity: 1; }
        }
      </style>
    `;
    
    if (cartForm) {
      const cartFooter = cartForm.querySelector('.cart__footer');
      if (cartFooter) {
        cartFooter.prepend(summary);
      } else {
        cartForm.appendChild(summary);
      }
      
      setTimeout(() => {
        const checkoutButtons = document.querySelectorAll('button[name="checkout"], input[name="checkout"]');
        checkoutButtons.forEach(btn => {
          if (!btn.dataset.enhanced) {
            btn.dataset.enhanced = "true";
            btn.innerHTML = '<span style="display: inline-flex; align-items: center; gap: 8px;">Complete Booking NOW <span style="animation: pulse 1.5s infinite;">⏱️</span></span>';
            btn.style.background = '#9a0705';
            btn.style.fontWeight = 'bold';
          }
        });
      }, 500);
      
    } else if (checkoutForm) {
      checkoutForm.prepend(summary);
      
      if (isCheckoutPage) {
        let timeLeft = 300;
        const timerEl = document.getElementById('checkout-timer');
        
        const checkoutTimerInterval = setInterval(() => {
          timeLeft--;
          
          if (timeLeft <= 0) {
            clearInterval(checkoutTimerInterval);
            if (timerEl) timerEl.textContent = "Time expired!";
            return;
          }
          
          const minutes = Math.floor(timeLeft / 60);
          const seconds = timeLeft % 60;
          if (timerEl) {
            timerEl.textContent = `${minutes}:${seconds.toString().padStart(2, '0')} remaining`;
            
            if (timeLeft < 60) {
              timerEl.style.color = '#ff0000';
              timerEl.style.fontWeight = 'bold';
              timerEl.style.animation = 'pulse 0.8s infinite';
            }
          }
        }, 1000);
      }
    }
  }

  storeBookingBannerData() {
    const bookingData = BookXLocationBooking.getBookingData();
    
    if (!bookingData || !bookingData.date) return;
    
    const [year, month, day] = bookingData.date.split('-').map(Number);
    const dateObj = new Date(year, month - 1, day);
    
    const options = { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' };
    const formattedDate = dateObj.toLocaleDateString('en-US', options);
    
    let message = `You're booking for ${formattedDate}`;
    if (bookingData.time) {
      message += ` & ${bookingData.time}`;
    }
    
    if (bookingData.location) {
      message += ` at ${bookingData.location}`;
    }
    
    localStorage.setItem('rockinRBookingMessage', message);
    localStorage.setItem('rockinRBookingDisplayNeeded', 'true');
    localStorage.setItem('rockinRBookingFormattedDate', formattedDate);
    
    if (!localStorage.getItem('bookingSessionTimeout') || !localStorage.getItem('bookingSessionStartTime')) {
      const randomMinutes = Math.floor(Math.random() * 6) + 10;
      const sessionTimeout = randomMinutes * 60;
      const startTime = Date.now();
      
      localStorage.setItem('bookingSessionTimeout', sessionTimeout.toString());
      localStorage.setItem('bookingSessionStartTime', startTime.toString());
    }
  }

  injectEarlyLoadingScript() {
    return;
  }

  setupCartListeners() {
    this.removeExistingCartListeners();
    
    document.addEventListener('submit', this.handleAddToCartForm.bind(this), true);
    document.addEventListener('click', this.handleAddToCartClick.bind(this), true);
    
    this.setupAjaxCartListeners();

    if (window.location.pathname.includes('/cart')) {
      setTimeout(() => {
        this.updateCartAttributes();
      }, 1000);
    }
    
    if (window.location.pathname.includes('/checkout')) {
      setTimeout(() => {
        this.attachBookingDataToCheckout();
      }, 1000);
    }
  }

  removeExistingCartListeners() {
    if (this.cartSubmitHandler) {
      document.removeEventListener('submit', this.cartSubmitHandler, true);
    }
    if (this.cartClickHandler) {
      document.removeEventListener('click', this.cartClickHandler, true);
    }
  }

  handleAddToCartClick(event) {
    const button = event.target;
    
    const isAddToCartButton = (
      button.name === 'add' ||
      button.type === 'submit' ||
      button.textContent?.toLowerCase().includes('add to cart') ||
      button.textContent?.toLowerCase().includes('add to bag') ||
      button.classList.contains('btn-cart') ||
      button.classList.contains('add-to-cart') ||
      button.hasAttribute('data-add-to-cart')
    );
    
    if (isAddToCartButton) {
      const bookingData = BookXLocationBooking.getBookingData();
      
      if (bookingData && bookingData.date) {
        const form = button.closest('form');
        if (form && form.action && form.action.includes('/cart/add')) {
          event.preventDefault();
          event.stopPropagation();
          this.submitCartFormWithBookingData(form, bookingData);
        }
      }
    }
  }

  handleAddToCartForm(event) {
    const form = event.target;
    
    if (form.tagName === 'FORM' && form.action && form.action.includes('/cart/add')) {
      const bookingData = BookXLocationBooking.getBookingData();
      
      if (bookingData && bookingData.date) {
        event.preventDefault();
        event.stopPropagation();
        
        this.submitCartFormWithBookingData(form, bookingData);
      }
    }
  }

  submitCartFormWithBookingData(form, bookingData) {
    const formattedData = this.formatBookingDataForCart(bookingData);
    const formData = new FormData(form);
    
    formData.append('properties[Booking Date]', formattedData.date);
    formData.append('properties[Booking Time]', formattedData.time);
    formData.append('properties[Location]', formattedData.location);
    formData.append('properties[Experience Type]', formattedData.experienceType);
    formData.append('properties[Booking ID]', formattedData.bookingId);
    formData.append('properties[Booking Phone]', bookingData.phone || 'Not provided');

    orderFormDataProperties(formData);

    fetch('/cart/add.js', {
      method: 'POST',
      body: formData
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      this.showLocationAlert(`Added to your ${formattedData.date} booking!`);
      
      setTimeout(() => {
        this.updateCartAttributes();
      }, 500);
      
    })
    .catch(error => {
      this.showLocationAlert(`Error adding to cart. Please try again.`);
      
      setTimeout(() => {
        const fallbackForm = document.createElement('form');
        fallbackForm.method = 'POST';
        fallbackForm.action = form.action;
        
        const originalFormData = new FormData(form);
        for (let [key, value] of originalFormData.entries()) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = value;
          fallbackForm.appendChild(input);
        }
        
        const bookingFields = [
          { name: 'properties[Booking Date]', value: formattedData.date },
          { name: 'properties[Booking Time]', value: formattedData.time },
          { name: 'properties[Location]', value: formattedData.location },
          { name: 'properties[Experience Type]', value: formattedData.experienceType },
          { name: 'properties[Booking ID]', value: formattedData.bookingId }
        ];
        
        bookingFields.forEach(field => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = field.name;
          input.value = field.value;
          fallbackForm.appendChild(input);
        });
        
        document.body.appendChild(fallbackForm);
        fallbackForm.submit();
      }, 1000);
    });
  }

  setupAjaxCartListeners() {
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const [resource, config] = args;
      
      if (resource && resource.includes && resource.includes('/cart/add') && config && config.body) {
        const bookingData = BookXLocationBooking.getBookingData();
        
        if (bookingData && bookingData.date && config.body instanceof FormData) {
          const instance = window.bookXInstance;
          const formattedData = instance ? instance.formatBookingDataForCart(bookingData) : {
            date: bookingData.date,
            location: bookingData.location || 'Not specified',
            experienceType: bookingData.isWeekend ? 'Weekend' : 'Weekday',
            bookingId: `BK${Date.now()}`
          };
          
          config.body.append('properties[Booking Date]', formattedData.date);
          config.body.append('properties[Location]', formattedData.location);
          config.body.append('properties[Experience Type]', formattedData.experienceType);
          config.body.append('properties[Booking ID]', formattedData.bookingId);
          config.body.append('properties[Booking Phone]', bookingData.phone || 'Not provided');

          orderFormDataProperties(config.body);
        }
      }
      
      return originalFetch.apply(window, args).then(response => {
        if (resource && resource.includes('/cart/add') && response.ok) {
          setTimeout(() => {
            if (window.bookXInstance) {
              window.bookXInstance.updateCartAttributes();
            }
          }, 500);
        }
        return response;
      });
    };
  }

  formatBookingDataForCart(bookingData) {
    const dateObj = new Date(bookingData.date);
    const options = { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' };
    const formattedDate = dateObj.toLocaleDateString('en-US', options);
    
    const bookingId = bookingData.bookingId || `BK${Date.now()}`;
    
    if (!bookingData.bookingId) {
      bookingData.bookingId = bookingId;
      localStorage.setItem('rockinRBookingData', JSON.stringify(bookingData));
    }
    
    return {
      date:           formattedDate,
      time:           bookingData.time || 'Not specified',
      location:       bookingData.location || 'Not specified',
      experienceType: bookingData.isWeekend ? 'Weekend' : 'Weekday',
      bookingId:      bookingId,
      rawDate:        bookingData.date,
      isWeekend:      bookingData.isWeekend
    };
  }

  updateCartAttributes() {
    const bookingData = BookXLocationBooking.getBookingData();
    if (!bookingData || !bookingData.date) return;
    
    const formattedData = this.formatBookingDataForCart(bookingData);
    
    const data = {
      attributes: {
        'Booking Date':       formattedData.date,
        'Booking Time':       formattedData.time,
        'Booking Location':   formattedData.location,
        'Experience Type':    formattedData.experienceType,
        'Booking ID':         formattedData.bookingId,
        'Raw Booking Date':   formattedData.rawDate,
        'Is Weekend Booking': formattedData.isWeekend ? 'Yes' : 'No',
        'Booking Phone':      bookingData.phone || 'Not provided',
        'Booking Timestamp':  bookingData.timestamp || new Date().toISOString()
      }
    };
    
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    })
    .then(response => response.json())
    .then(() => {
      this.populateOrderNotes(formattedData);
    })
    .catch(() => {});
  }

  populateOrderNotes(formattedData) {
    setTimeout(() => {
      const notesTextarea = document.querySelector('[name="note"]');
      if (notesTextarea && !notesTextarea.value.includes('Booking')) {
        const notesText = `Booking Details:\n- Date: ${formattedData.date}\n- Location: ${formattedData.location}\n- Type: ${formattedData.experienceType}\n- ID: ${formattedData.bookingId}`;
        
        if (notesTextarea.value.trim()) {
          notesTextarea.value = `${notesTextarea.value}\n\n${notesText}`;
        } else {
          notesTextarea.value = notesText;
        }
      }
    }, 1000);
  }

  attachBookingDataToCheckout() {
    const bookingData = BookXLocationBooking.getBookingData();
    if (!bookingData || !bookingData.date) return;
    
    const formattedData = this.formatBookingDataForCart(bookingData);
    
    const cartAttributes = {
      attributes: {
        'Booking Date':       formattedData.date,
        'Booking Time':       formattedData.time,
        'Booking Location':   formattedData.location,
        'Experience Type':    formattedData.experienceType,
        'Booking ID':         formattedData.bookingId,
        'Raw Booking Date':   formattedData.rawDate,
        'Is Weekend Booking': formattedData.isWeekend ? 'Yes' : 'No',
        'Booking Phone':      bookingData.phone || 'Not provided',
        'Booking Timestamp':  bookingData.timestamp || new Date().toISOString()
      },
      note: `Booking Details:
Date: ${formattedData.date}
Time: ${formattedData.time}
Location: ${formattedData.location}
Type: ${formattedData.experienceType}
ID: ${formattedData.bookingId}
Phone: ${bookingData.phone || 'Not provided'}
Weekend: ${formattedData.isWeekend ? 'Yes' : 'No'}`,
      note_attributes: [
        { 'Booking Date': formattedData.date },
        { 'Booking Time': formattedData.time },
        { 'Booking Location': formattedData.location },
        { 'Experience Type': formattedData.experienceType },
        { 'Booking ID': formattedData.bookingId },
        { 'Booking Phone': bookingData.phone || 'Not provided' },
        { 'Raw Booking Date': formattedData.rawDate },
        { 'Is Weekend Booking': formattedData.isWeekend ? 'Yes' : 'No' },
        { 'Booking Timestamp': bookingData.timestamp || new Date().toISOString() }
      ]
    };

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(cartAttributes)
    })
    .then(response => response.json())
    .then(() => {
      this.updateCheckoutForms(formattedData);
    })
    .catch(() => {});
  }

  updateCheckoutForms(formattedData) {
    const checkoutForms = document.querySelectorAll('form[action*="/cart"], form[action*="/checkout"]');
    
    checkoutForms.forEach(form => {
      const existingFields = form.querySelectorAll('[data-booking-field]');
      existingFields.forEach(field => field.remove());
      
      const fields = [
        { name: 'attributes[Booking Date]',       value: formattedData.date },
        { name: 'attributes[Location]',            value: formattedData.location },
        { name: 'attributes[Experience Type]',     value: formattedData.experienceType },
        { name: 'attributes[Booking ID]',          value: formattedData.bookingId },
        { name: 'attributes[Raw Booking Date]',    value: formattedData.rawDate },
        { name: 'attributes[Is Weekend Booking]',  value: formattedData.isWeekend ? 'Yes' : 'No' },
        { name: 'note', value: `Booking Details:
Date: ${formattedData.date}
Location: ${formattedData.location}
Type: ${formattedData.experienceType}
ID: ${formattedData.bookingId}
Weekend: ${formattedData.isWeekend ? 'Yes' : 'No'}` }
      ];
      
      fields.forEach(field => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = field.name;
        input.value = field.value;
        input.setAttribute('data-booking-field', 'true');
        form.appendChild(input);
      });
    });
  }
}

function getRandomCity() {
  const cities = [
    "Austin", "San Antonio", "Dallas", "Houston", "New Braunfels", 
    "San Marcos", "Waco", "Fort Worth", "Gruene", "Kyle"
  ];
  return cities[Math.floor(Math.random() * cities.length)];
}

window.changeBookingDate = function() {
  const bookingData = BookXLocationBooking.getBookingData();
  if (!bookingData || !bookingData.location) {
    alert('Sorry, we could not determine your booking location. Please try again.');
    return;
  }
  
  const location = bookingData.location;
  
  const locationPages = {
    'Gruene':     '/pages/gruene',
    'The Shore':  '/pages/the-shore',
    'The Double': '/pages/the-double',
    'Comal':      '/pages/comal',
    'Riverbank':  '/pages/riverbank',
    'Huaco':      '/pages/huaco-raft',
  };
  
  const fallbackPage = '/pages/locations';
  const pageUrl = locationPages[location] || fallbackPage;
  
  const alertEl = document.createElement('div');
  alertEl.innerHTML = `
    <div style="
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: white;
      border-radius: 8px;
      padding: 20px;
      max-width: 400px;
      text-align: center;
      box-shadow: 0 4px 20px rgba(0,0,0,0.2);
      z-index: 10000;
    ">
      <div style="font-size: 40px; margin-bottom: 10px;">📅</div>
      <h3 style="margin: 0 0 10px 0; color: #333;">Changing Booking Date</h3>
      <p style="margin: 0 0 20px 0; color: #666;">
        Taking you back to the booking calendar for<br>
        <strong>${location}</strong>
      </p>
      <div class="loading-dots" style="
        display: flex;
        justify-content: center;
        gap: 8px;
        margin-bottom: 15px;
      ">
        <span style="width:10px;height:10px;background:#9ABCC7;border-radius:50%;display:inline-block;animation:loading 1.4s infinite ease-in-out both;animation-delay:0s;"></span>
        <span style="width:10px;height:10px;background:#9ABCC7;border-radius:50%;display:inline-block;animation:loading 1.4s infinite ease-in-out both;animation-delay:0.2s;"></span>
        <span style="width:10px;height:10px;background:#9ABCC7;border-radius:50%;display:inline-block;animation:loading 1.4s infinite ease-in-out both;animation-delay:0.4s;"></span>
      </div>
      <div style="font-size: 14px; color: #999;">Please wait...</div>
    </div>
    <div style="
      position: fixed;
      top: 0; left: 0;
      width: 100%; height: 100%;
      background: rgba(0,0,0,0.5);
      z-index: 9999;
    "></div>
    <style>
      @keyframes loading {
        0%, 80%, 100% { transform: scale(0); }
        40% { transform: scale(1); }
      }
    </style>
  `;
  
  document.body.appendChild(alertEl);
  
  setTimeout(() => {
    window.location.href = pageUrl;
  }, 1500);
};

BookXLocationBooking.getBookingData = function() {
  try {
    const data = JSON.parse(localStorage.getItem('rockinRBookingData') || '{}');
    return Object.keys(data).length > 0 ? data : null;
  } catch (e) {
    return null;
  }
};

BookXLocationBooking.clearBookingData = function() {
  sessionStorage.removeItem('rockinRBookingData');
  localStorage.removeItem('rockinRBookingData');
  localStorage.removeItem('rockinRBookingMessage');
  localStorage.removeItem('rockinRBookingDisplayNeeded');
  localStorage.removeItem('rockinRBookingFormattedDate');
  localStorage.removeItem('bookingSessionTimeout');
  localStorage.removeItem('bookingSessionStartTime');
  localStorage.removeItem('bookingTimeRemaining');
  localStorage.removeItem('viewersShown');
  localStorage.removeItem('viewerCount');
};

BookXLocationBooking.isWeekendBooking = function() {
  const data = this.getBookingData();
  return data && data.isWeekend === true;
};

document.addEventListener('DOMContentLoaded', function() {
  var path = window.location.pathname;
  var isInBookingFlow = (
    path.includes('/pages/book') ||
    path.includes('/cart') ||
    path.includes('/checkout') ||
    path.includes('/pages/find-your-float')
  );
  try {
    var bookingData = BookXLocationBooking.getBookingData();
    if (!isInBookingFlow && bookingData) {
      fetch('/cart/clear.js', { method: 'POST' }).catch(function(){});
      BookXLocationBooking.clearBookingData();
    }
  } catch(e) {}

  window.bookXInstance = new BookXLocationBooking();
});

document.addEventListener('page:load page:change', function() {
  if (!window.bookXInstance) {
    window.bookXInstance = new BookXLocationBooking();
  } else {
    window.bookXInstance.init();
  }
});

window.addEventListener('popstate', function() {
  setTimeout(() => {
    if (!window.bookXInstance) {
      window.bookXInstance = new BookXLocationBooking();
    } else {
      window.bookXInstance.init();
    }
  }, 100);
});

let lastUrl = location.href;
new MutationObserver(() => {
  const url = location.href;
  if (url !== lastUrl) {
    lastUrl = url;
    setTimeout(() => {
      if (!window.bookXInstance) {
        window.bookXInstance = new BookXLocationBooking();
      } else {
        window.bookXInstance.init();
      }
    }, 500);
  }
}).observe(document, { subtree: true, childList: true });

window.resetBookXBooking = function() {
  BookXLocationBooking.clearBookingData();
  window.location.reload();
};